// The Connections widget's side of each computer's agent servers: what each last read (GET /api/agent-connections,
// the owner's alone), kept live by the agent-connections event, and a fresh look asked for when the full view opens.
// Nothing here holds a key: computers send names, states, hosts and tool names only (cleanConnections).

import { create } from "zustand";
import type { ComputerConnections } from "../../shared/types";
import { api } from "../lib/api";

interface AgentConnectionsState {
  /** null until the board has said */
  computers: ComputerConnections[] | null;
  error: string | null;
  /** when this page last asked the computers to look again */
  asked: number | null;
}

export const useAgentConnections = create<AgentConnectionsState>(() => ({ computers: null, error: null, asked: null }));

let loading: Promise<void> | null = null;

/** What each computer last read; once per page unless asked again. */
export function loadAgentConnections(again = false): Promise<void> {
  if (loading && !again) return loading;
  loading = api<{ computers: ComputerConnections[] }>("/api/agent-connections")
    .then((r) => useAgentConnections.setState({ computers: r.computers, error: null }))
    .catch((e) => {
      useAgentConnections.setState({ error: (e as Error).message });
      loading = null;
    });
  return loading;
}

/** Ask every linked computer to read its agents' servers again: each says so as it starts, and sends what it found. */
export async function lookAgain(): Promise<number> {
  useAgentConnections.setState({ asked: Date.now() });
  const r = await api<{ asked: number }>("/api/agent-connections/look", { body: {} });
  return r.asked;
}

/** One computer, read again or being read, from the board's event. */
export function onAgentConnections(computer: ComputerConnections) {
  useAgentConnections.setState((s) => {
    const list = s.computers ?? [];
    const i = list.findIndex((c) => c.id === computer.id);
    return { computers: i < 0 ? [...list, computer] : list.map((c, j) => (j === i ? computer : c)) };
  });
}
